import AuthService from "@/services/AuthService";
import * as DateFNS from "date-fns";
import Vue from "vue";
import { event } from "vue-gtag";
import { Module as Mod } from 'vuex';
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import store from "..";
import router from "@/router";

const storedUser = localStorage.getItem("user");

function isRefreshingTokenExpired(): boolean {
    const cookie = Vue.$cookies.get("refreshToken");
    if (cookie) {
        cookie.expired = DateFNS.isBefore(new Date(cookie.expdate), new Date(Date.now()));
        if (!cookie.expired)
            return false;
    }
    return true;
}

function isTokenExpired(): boolean {
    const cookie = Vue.$cookies.get("token");
    if (!cookie) return true;

    cookie.expired = DateFNS.isBefore(new Date(cookie.expdate), new Date(Date.now()));
    if (!cookie.expired) return false;

    return true;
}

@Module({ namespaced: true })
class Auth extends VuexModule {

    constructor(module: Mod<ThisType<any>, any>) {
        super(module);
    }

    public loginDialog = {
        visible: false
    };

    public user = storedUser ? JSON.parse(storedUser) : null;

    public status = storedUser
        ? {
            loggedIn: DateFNS.isBefore(new Date(this?.user?.expdate || 0), new Date(Date.now())) ? false : true,
            expired: this.user ? Math.floor((Date.now() - new Date(this.user.expdate).getTime()) / 60000) > 0 : true,
            canChangePwd: false,
            adminRole: this.user.role == "ADMIN",
        }
        : {
            loggedIn: false,
            expired: true,
            canChangePwd: false,
            adminRole: false,
        };

    @Mutation
    public loginSuccess(user: any): void {
        store.commit('User/registerDevice', user, { root: true });
        this.user = user;

        Vue.set(this, "status", {
            loggedIn: true,
            expired: false,
            canChangePwd: false,
            adminRole: this.user.role == "ADMIN",
        });
    }

    @Mutation
    public loginWithPwdChangeAccepted(user: any): void {
        store.commit('User/removeDevice', { root: true });
        this.user = user;
        this.status.loggedIn = false;
        this.status.expired = true;
        this.status.canChangePwd = true;
        this.status.adminRole = false;
    }

    @Mutation
    public logout(): void {
        store.commit('User/removeDevice', { root: true });
        this.user = null;
        this.status.loggedIn = false;
        this.status.expired = true;
        this.status.canChangePwd = false;
        this.status.adminRole = false;
    }

    @Action({ rawError: true })
    updateToken(responseData: any) {
        if (responseData && responseData.headers && responseData.headers.token) {
            const tokenCookie = Vue.$cookies.get("token");

            if (tokenCookie && DateFNS.differenceInMinutes(new Date(Date.now()), new Date(tokenCookie.creation)) != 0) {
                Vue.$cookies.set(
                    "token",
                    {
                        creation: new Date(Date.now()),
                        value: responseData.headers.token,
                    },
                    new Date(responseData.headers.tokenexp)
                );
            }
        }
    }

    @Mutation
    updateCookies(tokenResponseData: any) {
        const { expdate, token, refresh, refreshExpDate } = tokenResponseData;
        const dt = new Date(expdate);
        Vue.$cookies.set(
            "token",
            {
                creation: new Date(Date.now()),
                value: token,
                expdate: dt
            },
            dt
        );
        const refDt = new Date(refreshExpDate);
        Vue.$cookies.set(
            "refreshToken",
            {
                creation: new Date(Date.now()),
                value: refresh,
                expdate: refDt
            },
            refDt
        );
    }

    @Action({ rawError: true })
    async refreshToken(logoffIfExpired: boolean) {
        const tokenCk = Vue.$cookies.get("token");
        const rTokenCk = Vue.$cookies.get("refreshToken");

        if (tokenCk) {
            tokenCk.expired = DateFNS.isBefore(new Date(tokenCk.expdate), new Date(Date.now()));
            rTokenCk.expired = DateFNS.isBefore(new Date(rTokenCk.expdate), new Date(Date.now()));

            if (!tokenCk.expired) {
                return Promise.resolve(tokenCk.value);
            }
        }

        if (logoffIfExpired && (!tokenCk || tokenCk.expired) && (!rTokenCk || rTokenCk.expired)) {
            this.context.dispatch("signOut");
            return Promise.reject("Token expirado");
        }

        if ((!tokenCk || tokenCk.expired) && rTokenCk) {
            try {
                const newTokenResponse = await AuthService.refreshToken(rTokenCk);
                this.context.commit("updateCookies", newTokenResponse.data);
                this.status.loggedIn = !isTokenExpired() && !isRefreshingTokenExpired();
                this.status.expired = false;
                return Promise.resolve(newTokenResponse.data.token);
            } catch (error) {
                this.context.dispatch("signOut");
                router.replace("");
                const message = `Falha ao atualizar o token de autenticação`;
                return Promise.reject(message);
            }
        }
    }

    @Action({ rawError: true })
    login(data: any): Promise<any> {
        return AuthService.login(data).then(
            (response) => {
                switch (response.status) {
                    case 200:
                        if (!response.data.token) {
                            store.dispatch("Notifications/showErrorMessage", "Falha ao autenticar usuário", { root: true });
                            return Promise.reject("Falha ao autenticar usuário");
                        }

                        Vue.$cookies.set(
                            "token",
                            {
                                creation: new Date(Date.now()),
                                value: response.data.token,
                                expdate: new Date(response.data.expdate)
                            },
                            new Date(response.data.expdate)
                        );

                        Vue.$cookies.set(
                            "refreshToken",
                            {
                                creation: new Date(Date.now()),
                                value: response.data.refresh,
                                expdate: new Date(response.data.refreshExpDate)

                            },
                            new Date(response.data.refreshExpDate)
                        );

                        // Salva o usuário no local storage
                        localStorage.removeItem("user");
                        localStorage.setItem("user", JSON.stringify(response.data));

                        this.context.commit("loginSuccess", response.data);

                        store.dispatch('Pedido/buscarPedidoEdicao', { root: true });
                        store.dispatch(
                            "Notifications/showInfoMessage",
                            "Seja bem vindo, " + response.data.nome + "\n" + response.data.razao + " - " + response.data.cnpj,
                            { root: true }
                        );

                        event("login", { method: "password" });
                        return Promise.resolve(response);

                    case 202:
                        localStorage.removeItem("changeUser");
                        localStorage.setItem("changeUser", JSON.stringify(response.data));

                        this.context.commit("loginWithPwdChangeAccepted", response.data);
                        store.dispatch("Notifications/showInfoMessage", "Alteração de senha pendente", { root: true });
                        return Promise.resolve(response);
                }
            },
            (error) => {
                const message = error.response.data;
                this.context.dispatch("signOut");
                store.dispatch("Notifications/showErrorMessage", message, { root: true });
                return Promise.reject(message);
            }
        );
    }

    @Action
    signOut(message?: string): Promise<any> {
        console.log("signOut");
        if (message) {
            store.dispatch("Notifications/showErrorMessage", message, {
                root: true,
            });
        }

        Vue.$cookies.remove("token");
        Vue.$cookies.remove("refreshToken")
        localStorage.removeItem("user");
        this.context.commit("logout");
        this.context.commit("Pedido/setStoreInitialState", "", { root: true });

        return Promise.resolve(true);
    }

    @Action
    async loadLoggedUser() {
        // Verifica se o token expirou
        if (isTokenExpired() && !isRefreshingTokenExpired()) {
            await this.context.dispatch("refreshToken", false).catch(() => console.log("Token expirado"));
        } else if (isTokenExpired() && isRefreshingTokenExpired()) {
            await this.context.dispatch("signOut");
        }
    }

    get isLoggedIn(): boolean {
        console.log("isLoggedIn", this.status.loggedIn);
        return this.status.loggedIn;
    }

    get isChangePassword(): boolean {
        return this.status.canChangePwd;
    }

    get isUserAdmin(): boolean {
        return this.status.adminRole;
    }

    get userData(): any {
        return this.user;
    }

    get getLoginDialogStatus() {
        return this.loginDialog;
    }
}

export default Auth;
